import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './header.scss';
import UserIcon from '../../assets/dark-theme/icons/user-yellow.png';
import logout from '../../assets/dark-theme/icons/Logout.svg';
import { PageRoutesToModule } from '../../components/shared/modules';
import { useSelector } from 'react-redux';
import { RootState } from '../../state/features/reducers';
import { decodeToken } from '../../utils/tokenUtils';
import {
    ConfigProviderComponent,
    antdTheme,
} from '../../components/shared';
import { documentation } from '../../assets/dark-theme';
import { useSharedContext } from '../../components/shared/ContextProvider';
import { useTranslation } from 'react-i18next';
// Ant Design-
import { Dropdown, Tooltip, message } from 'antd';
import {
    CustomerServiceOutlined,
    MenuUnfoldOutlined,
    MenuFoldOutlined,
    KeyOutlined,
    SettingOutlined,
} from '@ant-design/icons';

interface HeaderProps {
    onLogoutClick?: () => void;
    onToggle?: () => void;
    mobileCollapse: boolean;
}
interface DecodedToken {
    email?: string;
    [key: string]: any;
}

const baseProductImageClass = 'productIcon svg';

const Header: React.FC<HeaderProps> = ({
    onLogoutClick = () => {},
    onToggle = () => {},
    mobileCollapse,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const user = useSelector(
        (state: RootState) => state.auth.user || {},
    );
    const { sharedContext, handleContextChange } = useSharedContext();
    const { t } = useTranslation();

    // Extract the breadcrumb from the path
    const breadCrumbArr = location?.pathname?.split('/') ?? [];

    // Determine if we're on a run details page
    const isRunDetails =
        breadCrumbArr.includes('runs') &&
        breadCrumbArr.includes('facility');

    // Use the appropriate breadcrumb based on the page type
    let breadCrumb: string, pageTitle: string, module: string;

    if (isRunDetails) {
        // For run details pages, use 'runs' as the base breadcrumb
        breadCrumb = 'runs';

        // Check if we have a specific submenu selected (from sharedContext)
        const runConfig =
            PageRoutesToModule[
                breadCrumb as keyof typeof PageRoutesToModule
            ];
        if (sharedContext?.currentMenu) {
            const subMenuConfig =
                PageRoutesToModule[
                    sharedContext.currentMenu as keyof typeof PageRoutesToModule
                ];
            pageTitle =
                (runConfig as any).title +
                ((subMenuConfig as any)?.title || '');
        } else {
            pageTitle = (runConfig as any).title;
        }

        module = (runConfig as any).module;
    } else {
        // For all other pages, use the last segment of the path
        breadCrumb =
            breadCrumbArr?.[breadCrumbArr?.length - 1] ?? 'dashboard';
        const moduleConfig =
            PageRoutesToModule[
                breadCrumb as keyof typeof PageRoutesToModule
            ];

        pageTitle = breadCrumb;
        module = 'basic';

        if (moduleConfig && 'title' in moduleConfig) {
            // Only include submenu if we're on a run details page
            pageTitle = moduleConfig.title;
            module = moduleConfig.module;
        }
    }

    // Clear the current menu when navigating away from run details
    useEffect(() => {
        // If we're not on a run details page and there's a current menu set, clear it
        if (!isRunDetails && sharedContext?.currentMenu) {
            handleContextChange({
                ...sharedContext,
                currentMenu: undefined,
            });
        }
    }, [
        location.pathname,
        isRunDetails,
        sharedContext,
        handleContextChange,
    ]);

    const moduleIcon = `${baseProductImageClass} ${module}`;
    let email = '';

    if (user?.idToken) {
        const userData = decodeToken(user?.idToken) as DecodedToken;
        if (userData?.email) email = userData.email;
    }

    const handleAccessTokenClick = () => {
        if (user?.accessToken) {
            navigator.clipboard
                .writeText(user.accessToken)
                .then(() => {
                    message.success(
                        'Access token copied to clipboard',
                    );
                })
                .catch(() => {
                    message.error('Failed to copy access token');
                });
        } else {
            message.warning('No access token available');
        }
    };

    const handleProfileClick = () => {
        navigate('/profile');
    };

    let apiDocsUrl = '';
    try {
        const strippedAPIURL = new URL(user.apiURL!)?.origin; // Extract the base URL from the API URL
        const customerApiBase =
            user?.customerApiBase || strippedAPIURL;
        apiDocsUrl = customerApiBase + '/swagger/';
    } catch (error) {
        console.error('Error extracting API URL:', error);
    }

    const items: any[] = [
        {
            label: (
                <a
                    href="https://customerdocs.liveline.cloud/index.html"
                    target={'_blank'}
                    rel="noreferrer"
                >
                    <div className="profileMenuItem">
                        <img
                            src={documentation}
                            alt="documentation"
                            className="menuIcon"
                        />
                        <span className="menuName">
                            {' '}
                            Documentation
                        </span>
                    </div>
                </a>
            ),
            key: '0',
        },
        {
            label: (
                <a
                    href="https://liveline.atlassian.net/servicedesk/customer/portals"
                    target={'_blank'}
                    rel="noreferrer"
                >
                    <div className="profileMenuItem">
                        <CustomerServiceOutlined
                            style={{ fontSize: 16 }}
                            className="menuIcon"
                        />
                        <span className="menuName"> Contact Us</span>
                    </div>
                </a>
            ),
            key: '1',
        },
        {
            label: (
                <div
                    className="profileMenuItem"
                    onClick={handleProfileClick}
                >
                    <SettingOutlined
                        style={{ fontSize: 16 }}
                        className="menuIcon"
                    />
                    <span className="menuName">Account Settings</span>
                </div>
            ),
            key: '2',
        },
        {
            label: (
                <div
                    className="profileMenuItem"
                    onClick={handleAccessTokenClick}
                >
                    <KeyOutlined
                        style={{ fontSize: 16 }}
                        className="menuIcon"
                    />
                    <span className="menuName">
                        Copy Access Token
                    </span>
                </div>
            ),
            key: '3',
        },
        {
            label: (
                <a
                    href={apiDocsUrl}
                    target={'_blank'}
                    rel="noreferrer"
                >
                    <div className="profileMenuItem">
                        <span className="menuName">
                            {' '}
                            API Documentation
                        </span>
                    </div>
                </a>
            ),
            key: '4',
        },
        {
            label: (
                <div
                    className="profileMenuItem"
                    onClick={onLogoutClick}
                >
                    <img
                        src={logout}
                        className="menuIcon"
                        title="logout"
                        alt="user icon"
                    />
                    <span className="menuName">Logout</span>
                </div>
            ),
            key: '5',
        },
    ];

    // Controls for the show/hide of the left nav menu-
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const IconComponent = mobileCollapse
        ? MenuUnfoldOutlined
        : MenuFoldOutlined;
    const tooltipText = mobileCollapse ? 'Show Menu' : 'Hide Menu';

    return (
        <div className="headerContainer">
            <Tooltip
                title={tooltipText}
                open={tooltipVisible}
                onOpenChange={setTooltipVisible}
                key={mobileCollapse ? 'closed' : 'open'}
                placement="right"
            >
                <IconComponent
                    style={{ fontSize: 20, cursor: 'pointer' }}
                    onClick={() => {
                        onToggle();
                        setTooltipVisible(false); // Hide tooltip on toggle-
                    }}
                />
            </Tooltip>
            <div className="headerTitle">
                {moduleIcon && (
                    <span
                        className={`${moduleIcon} moduleIcon`}
                        title={module}
                    >
                        &nbsp;&nbsp;
                    </span>
                )}
                <span className="currentPage">{`${pageTitle}`}</span>
            </div>
            <div className="userName profileUserName">{email}</div>
            <div className="headerUserProfile">
                <div className="userIcon">
                    <ConfigProviderComponent theme={antdTheme}>
                        <Dropdown
                            menu={{
                                items,
                            }}
                            trigger={['click']}
                            placement="bottomRight"
                            arrow
                        >
                            <img
                                src={UserIcon}
                                className="userImg"
                                title={email}
                                alt="user icon"
                            />
                        </Dropdown>
                    </ConfigProviderComponent>
                </div>
            </div>
        </div>
    );
};

export default Header;
